/* Customer-experience Cycle 2, Step 1 -- full-screen search overlay.
 *
 * Predictive results come from Shopify's own native predictive-search
 * endpoint (/search/suggest.json) -- no custom search backend, no app. The
 * JSON shape (per-product price/price_min/price_max/featured_image) is
 * exactly what Shopify's platform returns; nothing here is a guess.
 *
 * Price rule matches snippets/product-card.liquid exactly: show the
 * product's lowest variant price, prefixed "From " only when it actually
 * varies (price_min !== price_max) -- the same rule Step 3 applies to grid
 * cards and the PDP, so a result here never contradicts its own card.
 *
 * Carat is deliberately NOT shown: predictive search has no clean field for
 * it (only buried in the HTML `body` blob), and scraping that would be
 * fragile and could show the wrong number. Per the brief's own "if already
 * on the card" qualifier, omitting it is the honest choice.
 */
(function () {
  var overlay = document.querySelector("[data-search-overlay]");
  if (!overlay) return;

  var input = overlay.querySelector("[data-search-overlay-input]");
  var closeBtns = overlay.querySelectorAll("[data-search-overlay-close]");
  var emptyEl = overlay.querySelector("[data-search-overlay-empty]");
  var statusEl = overlay.querySelector("[data-search-overlay-status]");
  var resultsEl = overlay.querySelector("[data-search-overlay-results]");
  var seeAllEl = overlay.querySelector("[data-search-overlay-seeall]");
  var seeAllLink = overlay.querySelector("[data-search-overlay-seeall-link]");
  var searchUrl = overlay.querySelector("[data-search-overlay-form]").getAttribute("action");

  var triggers = document.querySelectorAll("[data-search-overlay-trigger]");
  var closeTimer = null;

  function inr(v) {
    var n = Math.round(parseFloat(v) || 0);
    return "₹" + n.toLocaleString("en-IN");
  }

  function clear(el) { while (el && el.firstChild) el.removeChild(el.firstChild); }
  function text(tag, className, str) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (str != null) el.appendChild(document.createTextNode(str));
    return el;
  }

  /* Cycle 7 -- wishlist heart on overlay result rows. Same markup contract as
   * the PDP and product-card hearts (data-wishlist-heart + namespaced
   * data-wish-*), driven by the same assets/svz-wishlist.js client through its
   * delegated click handler. No second wishlist client, no new endpoint.
   *
   * The heart is a SIBLING of the result link, never a child: a <button>
   * inside an <a> is invalid HTML and clicking it would also navigate to the
   * product. That is the only reason the row wrapper below exists.
   *
   * Nothing else in this file changed -- search, chips, predictive fetch, the
   * price rule and the Verify-it link are untouched.
   */
  var HEART_SVG =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">' +
    '<path d="M12 21s-7-4.35-9.5-9C1 8 3.5 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20.5 4 23 8 21.5 12c-2.5 4.65-9.5 9-9.5 9Z" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function resultHeart(item) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "search-ov__result-wish";
    btn.setAttribute("data-wishlist-heart", "");
    btn.setAttribute("data-wish-id", String(item.id == null ? "" : item.id));
    btn.setAttribute("data-wish-title", item.title || "");
    /* Predictive search reports price_min in RUPEES ("25540.00"), while the
     * Liquid hearts pass Shopify's integer PAISE and svz-account.js renders
     * the Wishlist row as inr(w.price / 100). Convert here so exactly one
     * unit is stored no matter which heart saved the product. */
    btn.setAttribute("data-wish-price", String(Math.round((parseFloat(item.price_min) || 0) * 100)));
    btn.setAttribute("data-wish-image", (item.featured_image && item.featured_image.url) || "");
    btn.setAttribute("data-wish-handle", item.handle || "");
    btn.setAttribute("aria-label", "Save " + (item.title || "this piece") + " to wishlist");
    btn.setAttribute("aria-pressed", "false");
    btn.innerHTML = HEART_SVG;
    return btn;
  }

  function resultCard(item) {
    var row = document.createElement("div");
    row.className = "search-ov__result-row";

    var a = document.createElement("a");
    a.className = "search-ov__result";
    a.href = item.url;

    var img = document.createElement("img");
    img.className = "search-ov__result-img";
    img.loading = "lazy";
    img.alt = (item.featured_image && item.featured_image.alt) || item.title;
    var src = item.featured_image && item.featured_image.url;
    if (src) img.src = src + (src.indexOf("?") === -1 ? "?width=160" : "&width=160");
    a.appendChild(img);

    var meta = text("span", "search-ov__result-meta");
    meta.appendChild(text("span", "search-ov__result-title", item.title));
    var varies = item.price_min !== item.price_max;
    meta.appendChild(text("span", "search-ov__result-price", (varies ? "From " : "") + inr(item.price_min)));
    a.appendChild(meta);

    row.appendChild(a);
    row.appendChild(resultHeart(item));
    return row;
  }

  var controller = null;
  function runSearch(q) {
    if (controller) controller.abort();
    if (!q) {
      show(emptyEl);
      hide(statusEl); hide(resultsEl); hide(seeAllEl);
      return;
    }
    hide(emptyEl);
    show(statusEl);
    statusEl.textContent = "Searching…";

    controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
    var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + "search/suggest.json"
      : "/search/suggest.json";
    url += "?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last";

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        clear(resultsEl);
        if (!products.length) {
          hide(statusEl);
          show(resultsEl);
          resultsEl.appendChild(text("p", "search-ov__status", "No results for “" + q + "”."));
          hide(seeAllEl);
          return;
        }
        hide(statusEl);
        products.forEach(function (p) { resultsEl.appendChild(resultCard(p)); });
        /* Paint saved state onto the hearts just inserted. The wishlist client
         * owns the session read and memoises it, so this is not a fetch per
         * keystroke. Guarded because the overlay must keep working even if
         * svz-wishlist.js is absent. */
        if (window.SvzWishlist && window.SvzWishlist.refresh) window.SvzWishlist.refresh(resultsEl);
        show(resultsEl);
        if (seeAllLink) seeAllLink.href = searchUrl + "?q=" + encodeURIComponent(q);
        show(seeAllEl);
      })
      .catch(function (e) {
        if (e && e.name === "AbortError") return;
        hide(statusEl);
        hide(resultsEl);
        hide(seeAllEl);
        show(emptyEl);
      });
  }

  function hide(el) { if (el) el.hidden = true; }
  function show(el) { if (el) el.hidden = false; }

  var debounceTimer = null;
  if (input) {
    input.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      var q = input.value.trim();
      debounceTimer = setTimeout(function () { runSearch(q); }, 250);
    });
  }

  function openOverlay() {
    clearTimeout(closeTimer);
    overlay.hidden = false;
    // Force layout so the browser registers the pre-open state before the
    // data-open transition kicks in, instead of skipping straight to it.
    void overlay.offsetHeight;
    overlay.setAttribute("data-open", "1");
    document.body.style.overflow = "hidden";
    if (input) { input.value = ""; input.focus(); }
    show(emptyEl);
    hide(statusEl); hide(resultsEl); hide(seeAllEl);
  }

  function closeOverlay() {
    overlay.removeAttribute("data-open");
    document.body.style.overflow = "";
    closeTimer = setTimeout(function () { overlay.hidden = true; }, 350);
  }

  triggers.forEach(function (btn) {
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      openOverlay();
    });
  });

  closeBtns.forEach(function (el) {
    el.addEventListener("click", closeOverlay);
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && overlay.hasAttribute("data-open")) closeOverlay();
  });
})();
